import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { useSelector } from 'react-redux';

import Button, { ButtonTypes } from '@Components/Buttons/Button';
import FormField from '@Components/forms/FormField';
import Checkbox from '@Components/forms/inputs/Checkbox';
import FormSliderInput from '@Components/forms/inputs/FormSliderInput';
import TextInput, { TextInputProps } from '@Components/forms/inputs/TextInput';
import Icon, { IconSizes } from '@Components/Icon';
import InfoMessage, { MessageTypes } from '@Components/InfoMessage';
import Col from '@Components/layout/Col';
import Row from '@Components/layout/Row';
import Paper, { PaperWidth } from '@Components/Paper';
import PaperBody, { PaperBodySpacing } from '@Components/PaperBody';
import Typography from '@Components/Typography/Typography';
import { useCurrency } from '@Hooks/useCurrency';
import { useTranslations } from '@Hooks/useTranslations';
import SectionTitle from '@Routes/static/LandingPage/SectionTitle';
import { getAppConfig, getCurrentLanguage } from '@Store/app/app.selectors';
import { validate, validateEmail, validatePhone, validateRequired } from '@Utils/form';
import { convertCentsToMoney, getTotalLoanCost } from '@Utils/money';
import { noop } from '@Utils/util';

import styles from './LoanForm.module.scss';

type LoanFormProps = {
  onSubmit: (values: LoanFormValues) => void;
  initialValues: LoanFormValues;
  isSuccess: boolean;
};

export type LoanFormValues = {
  amount: number;
  monthlyPayment: number;
  contactType: string;
  contactDetails: string;
};

const renderForm = (props: FormRenderProps<LoanFormValues> & { isSuccess: boolean }) => {
  const t = useTranslations();
  const appConfig = useSelector(getAppConfig);
  const currentLanguage = useSelector(getCurrentLanguage);
  const currency = useCurrency();

  const formState = props.form.getState().values;
  const { monthlyPayment, amount } = formState;

  const loanPrice = getTotalLoanCost({
    monthlyPayment,
    amount,
    interest: parseFloat(appConfig.advertLoanInterestRate),
  });

  const loanAmountInMoney = parseInt(convertCentsToMoney(appConfig.maxAdvertLoanAmount), 10);

  const onRadioClick = () => {
    props.form.resetFieldState('contactDetails');
    props.form.change('contactDetails', '');
  };

  return (
    <form onSubmit={props.handleSubmit}>
      <Paper width={PaperWidth.full}>
        <PaperBody spacing={PaperBodySpacing.l} className={styles.container}>
          <SectionTitle msg={t('msg_loan_form_title')} tag="h4" />
          <Typography msg={t('msg_loan_form_how_to')} tag="p" />
          <Col size={12} className={styles.sliderRow}>
            <Row justifyBetween size="sizeFull">
              <Typography msg={t('msg_loan_form_loan_sum')} tag="p" />
              <Typography msg={`${amount} ${currency}`} tag="p" bold />
            </Row>

            <Row size="sizeFull">
              <FormField name="amount" component={FormSliderInput} min={1000} max={loanAmountInMoney} step={1000} />
            </Row>
          </Col>
          <Col size={12} className={styles.sliderRow}>
            <Row justifyBetween size="sizeFull">
              <Typography msg={t('msg_loan_form_monthly_payment')} tag="p" />
              <Typography msg={`${monthlyPayment} ${currency}`} tag="p" bold />
            </Row>

            <Row>
              <FormField
                name="monthlyPayment"
                component={FormSliderInput}
                min={10}
                max={Math.round(loanAmountInMoney / appConfig.maxAdvertLoanDuration)}
                step={10}
              />
            </Row>
          </Col>
          <Row size="sizeFull" className={styles.loanPrice}>
            <Typography msg={t('msg_loan_form_loan_price')} tag="p" />
            <Typography msg={`${loanPrice} ${currency}`} tag="p" bold color="primary" />
          </Row>

          <div className={styles.separator} />

          <Col className={styles.radioContainer}>
            <Typography msg={t('msg_loan_form_contact')} tag="p" />
            <Row className={styles.radioRow}>
              <FormField
                name="contactType"
                component={Checkbox}
                type="radio"
                label={t('msg_loan_form_phone')}
                value="phone"
                bottomSpacing={false}
                onChange={onRadioClick}
              />
              <FormField
                name="contactType"
                component={Checkbox}
                type="radio"
                label={t('msg_loan_form_email')}
                value="email"
                bottomSpacing={false}
                onChange={onRadioClick}
              />
            </Row>
          </Col>

          <FormField<TextInputProps<string>>
            name="contactDetails"
            type="text"
            component={TextInput}
            placeholder={
              formState.contactType === 'email' ? t('msg_loan_form_enter_email') : t('msg_loan_form_enter_phone')
            }
            bottomSpacing={false}
            validate={validate(
              validateRequired(t),
              formState.contactType === 'email' ? validateEmail(t) : validatePhone(t, currentLanguage),
            )}
          />
          <Col size={5}>
            <Button type={ButtonTypes.submit} label={t('msg_loan_form_get_offer_label')} />
          </Col>

          {props.isSuccess && (
            <Col constant textCenter>
              <Icon icon="green-check-circle" size={IconSizes.m} className={styles.circleIcon} />
              <Typography msg={t('msg_advert_loan_form_success_message')} tag="p" />
            </Col>
          )}

          {props.submitError && <InfoMessage type={MessageTypes.error} message={props.submitError} />}

          <div className={styles.separator} />

          <Typography msg={t('msg_loan_form_example')} tag="p" size="s" color="gray" />
        </PaperBody>
      </Paper>
    </form>
  );
};

const LoanForm = ({ onSubmit = noop, initialValues, isSuccess }: LoanFormProps) => {
  return (
    <Form
      onSubmit={onSubmit}
      render={props => renderForm({ ...props, isSuccess })}
      initialValues={initialValues}
      isSuccess={isSuccess}
    />
  );
};

export default LoanForm;
